import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router";
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import type { AccountLoginViewModel } from "@repo/types/viewModels";
import type { AccountLoginPrincipalResponse } from "@repo/types/responses";
import type { UserPrincipal } from "@repo/types/identity";
import Label from "../components/form/Label";
import Input from "../components/form/input/InputField";
import Button from "../components/ui/button/Button";
import { useApiAccess, useAuth } from "@repo/hooks/contexts";
import { useDialog } from "@/hooks/contexts";
import { useLogin } from "@repo/hooks/queries";
import { ClientPlatform } from "@repo/types/client";

const Login = () => {
  const location = useLocation();
  const { user: userPrincipal, setUserPrincipal } = useAuth();
  const { setAccessToken } = useApiAccess();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AccountLoginViewModel>({
    mode: "onChange",
    defaultValues: {
      Username: "",
      Password: "",
      LoginType: 1,
      Platform: ClientPlatform.DesktopWeb,
    },
  });

  const dialog = useDialog();
  const { mutateAsync: login } = useLogin();

  const onLogin = async (data: AccountLoginViewModel) => {
    try {
      const loginResp: AccountLoginPrincipalResponse = await login(data);

      if (!loginResp.Result) {
        dialog.show({
          title: "Login Failed",
          message: "Invalid username or password. Please try again.",
          type: loginResp.Result ? "success" : "error",
          confirmText: "Ok",
        });

        return;
      }

      const userInfo: UserPrincipal = {
        UserId: loginResp.UserId,
        SessionId: loginResp.SessionId,
        Name: loginResp.Name,
        Email: loginResp.Email,
        ProfilePicPath: loginResp.ProfilePicPath,
        Roles: loginResp.Roles,
      };

      setUserPrincipal(userInfo);
      setAccessToken(loginResp.Token);

      navigate("/tickets");
    } catch {
      // Optionally handle error here
    }
  };

  if (userPrincipal) {
    const redirectPath = location.state?.from?.pathname || "/tickets";
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <>
      <div className="relative p-6 bg-white z-1 sm:p-0">
        <div className="relative flex flex-col justify-center w-full h-screen lg:flex-row sm:p-0">
          <div className="flex flex-col flex-1">
            <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto">
              <div>
                <div className="mb-5 sm:mb-8">
                  <h1 className="mb-2 font-semibold text-gray-800 text-4xl text-title-sm sm:text-title-md">
                    Log In
                  </h1>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Enter your email and password to log in!
                  </p>
                </div>
                <div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5"></div>
                  <form>
                    <div className="space-y-6">
                      <input
                        type="hidden"
                        value={1}
                        {...register("LoginType")}
                      />
                      <div>
                        <Label className="font-semibold">
                          Username{" "}
                          <span className="text-error-500">*</span>{" "}
                        </Label>
                        <Input
                          placeholder="Username"
                          error={!!errors.Username}
                          errorMessage={errors.Username?.message}
                          {...register("Username", {
                            required: "Username is required",
                            pattern: {
                              value:
                                /^([\w-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([\w-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/,
                              message: "Invalid Email address",
                            },
                          })}
                        />
                      </div>
                      <div>
                        <Label className="font-semibold">
                          Password{" "}
                          <span className="text-error-500">*</span>{" "}
                        </Label>
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter your password"
                            error={!!errors.Password}
                            errorMessage={errors.Password?.message}
                            {...register("Password", {
                              required: "Password is required",
                            })}
                          />

                          <span
                            onClick={() => setShowPassword(!showPassword)}
                            className={`absolute z-30 -translate-y-1/2 cursor-pointer right-4 ${
                              errors.Password ? "top-1/3" : "top-1/2"
                            }`}
                          >
                            {showPassword ? (
                              <Eye className="fill-gray-500 size-5" />
                            ) : (
                              <EyeOff className="fill-gray-500 size-5" />
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <Link
                          to="/reset-password"
                          className="text-sm text-brand-500 hover:text-brand-600"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <div>
                        <Button
                          type="submit"
                          className="w-full"
                          size="md"
                          onClick={handleSubmit(onLogin)}
                        >
                          Log in
                        </Button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>

          <div className="items-center hidden w-full h-full lg:w-1/2 bg-blue-950 lg:grid">
            <div className="relative flex items-center justify-center z-1">
              <div className="flex flex-col items-center max-w-xs">
                <Link to="/" className="block mb-4">
                  <img
                    width={231}
                    height={48}
                    src="/images/logo/auth-logo.png"
                    alt="Logo"
                  />
                </Link>
                <p className="text-center text-gray-400 dark:text-white/60">
                  ©2025 All Rights Reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export { Login };
